import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { Stethoscope, FileText, Activity, Users, ClipboardCheck, HeartHandshake } from "lucide-react";
import { trackAndNavigate } from "../utils/navigate";

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero Section */}
      <div className="bg-white py-24 lg:py-32">
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <h1 className="text-4xl lg:text-6xl font-bold text-[#012d5c] mb-6">
              About Integro Clinics
            </h1>
            <p className="text-xl lg:text-2xl text-[#005eb8] font-medium relative inline-block">
              Pioneering patient-first care in medical cannabis treatment
              <span className="absolute bottom-0 left-0 w-full h-1 bg-[#2d8c6e] rounded-full opacity-50"></span>
            </p>
          </Reveal>
        </div>
      </div>

      {/* Section 1: Our Mission */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl font-bold text-[#012d5c] mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Integro Clinics, we specialise in treating patients with complex conditions where traditional treatments may have fallen short. Our expert team of consultants spans pain medicine, psychiatry, oncology, women's health, spinal care, palliative medicine, and more.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Stethoscope className="w-8 h-8" />, title: "Specialist Consultants", text: "Multidisciplinary team of GMC-registered specialists" },
                { icon: <ClipboardCheck className="w-8 h-8" />, title: "Personalised Plans", text: "Tailored treatment protocols for individual needs" },
                { icon: <FileText className="w-8 h-8" />, title: "Evidence-Based Care", text: "Treatment decisions grounded in clinical research" }
              ].map((card, i) => (
                <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
                  <div className="bg-[#e0f2fe] text-[#005eb8] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#012d5c] mb-3">{card.title}</h3>
                  <p className="text-gray-500">{card.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 2: Patient First Philosophy */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-[#012d5c] mb-4">Patient First Philosophy</h2>
              <p className="text-lg text-gray-600">Our care model puts you at the centre, featuring:</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {[
                { icon: <Users />, title: "Dedicated Care Team", text: "Our clinic team is here Monday to Friday, 9am–5pm." },
                { icon: <Activity />, title: "Tailored Treatments", text: "Personalised plans combining licensed & unlicensed medicines" },
                { icon: <HeartHandshake />, title: "Collaborative Care", text: "Seamless coordination with your existing healthcare providers" }
              ].map((block, i) => (
                <div key={i} className="flex flex-col items-center text-center p-6">
                  <div className="text-[#4caf50] mb-4 p-3 bg-[#e8f5e9] rounded-lg">
                    {React.cloneElement(block.icon, { className: "w-8 h-8" })}
                  </div>
                  <h3 className="text-lg font-bold text-[#012d5c] mb-2">{block.title}</h3>
                  <p className="text-gray-600">{block.text}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#e0f2fe]/50 border-l-4 border-[#2d8c6e] p-6 rounded-r-lg max-w-3xl mx-auto text-center">
              <p className="text-[#005eb8] font-medium">All treatment plans follow NICE guidelines and MHRA regulations</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="bg-[#012d5c] py-16 text-center text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#012d5c] to-[#005eb8] opacity-50"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-2xl font-bold mb-4">Providing safe, legal access to medical cannabis for patients across the UK.</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Our team of specialists is here to help you on your journey to better health and improved quality of life.
          </p>
          <Button 
            className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-8 py-6 rounded-lg text-lg shadow-lg font-bold"
            onClick={() => trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' })}
          >
            Am I Eligible?
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
